import React from "react";
import { Icon } from "@iconify/react";
import PageHeader from "./PageHeader";

const DataTable = ({
  title,
  data = [],
  columns = [],
  actions = [],
  bulkActions = [],
  rowKey = "id",
  searchTerm = "",
  onSearchChange = () => {},
  page = 1,
  limit = 10,
  total = 0,
  onPageChange,
  onLimitChange,
  pagination = true,
  loading = false,
  headerActions = [],
  showCheckbox = true,
  sortConfig = { key: null, direction: 'asc' },
  onSortChange,
}) => {
  /* ================= CALCULATIONS ================= */
  const totalColSpan = columns.length + (showCheckbox ? 1 : 0) + (actions.length > 0 ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const [selectedRows, setSelectedRows] = React.useState([]);

  /* ================= PAGINATION LOGIC (NEW) ================= */
  const getPaginationGroup = () => {
    const pageRange = 2; // How many neighbors to show around current page
    let start = Math.max(1, page - pageRange);
    let end = Math.min(totalPages, page + pageRange);

    const pages = [];
    
    // Always show first page
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    // Show current page and neighbors
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Always show last page
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  /* ================= HANDLERS ================= */
  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedRows.length === data.length && data.length > 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(data.map((row) => row[rowKey]));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    if (onSortChange) onSortChange({ key, direction });
  };

  return (
    <>
      <PageHeader title={title} />
      <div className="card radius-12 border-0 shadow-none bg-base">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3 bg-base border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {pagination && (
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light text-sm text-nowrap">Show</span>
                <select
                  className="form-select form-select-sm w-auto bg-base border text-secondary-light"
                  value={limit}
                  onChange={(e) => onLimitChange && onLimitChange(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="icon-field">
              <input
                type="text"
                className="form-control form-control-sm w-auto"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <span className="icon"><Icon icon="ion:search-outline" /></span>
            </div>

            {showCheckbox && selectedRows.length > 0 && bulkActions.length > 0 && (
              <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2">
                <span className="text-sm fw-bold text-primary-600 text-nowrap">{selectedRows.length} selected</span>
                {bulkActions.map((action, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${action.className} radius-8 py-1 px-3 d-flex align-items-center gap-2`}
                    onClick={() => {
                      action.onClick(selectedRows);
                      setSelectedRows([]);
                    }}
                  >
                    {action.icon && <Icon icon={action.icon} />}
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3">
            {headerActions.map((action, i) => (
              <button key={i} className={`btn btn-sm ${action.className}`} onClick={action.onClick}>
                {action.icon && <Icon icon={action.icon} />} {action.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table borderless-table mb-0">
              <thead>
                <tr>
                  {showCheckbox && (
                    <th className="text-secondary-light fw-bold">
                      <div className="form-check style-check d-flex align-items-center">
                        <input className="form-check-input" type="checkbox" checked={data.length > 0 && selectedRows.length === data.length} onChange={toggleAll} />
                        <label className="form-check-label">S.L</label>
                      </div>
                    </th>
                  )}
                  {columns.map((col) => (
                    <th 
                      key={col.key} 
                      onClick={() => col.sortable !== false && handleSort(col.key)} 
                      className={`text-secondary-light fw-bold uppercase ${col.sortable !== false ? 'cursor-pointer' : ''}`}
                    >
                      <div className="d-flex align-items-center gap-2">
                        {col.label}
                        {col.sortable !== false && (
                          <Icon 
                            icon={sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? "lucide:arrow-up" : "lucide:arrow-down") : "lucide:chevrons-up-down"} 
                            className={sortConfig.key === col.key ? "text-primary-600" : "text-neutral-300"} 
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  {actions.length > 0 && <th className="text-secondary-light fw-bold text-end">Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalColSpan} className="text-center py-80">
                      <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-3xl mb-2" />
                      <p className="text-secondary-light">Fetching data...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr><td colSpan={totalColSpan} className="text-center py-80 text-secondary-light">No records found.</td></tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={row[rowKey] ?? idx}>
                      {showCheckbox && (
                        <td>
                          <div className="form-check style-check d-flex align-items-center">
                            <input className="form-check-input" type="checkbox" checked={selectedRows.includes(row[rowKey])} onChange={() => toggleRow(row[rowKey])} />
                            <label className="form-check-label">{String((page - 1) * limit + (idx + 1)).padStart(2, "0")}</label>
                          </div>
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.key} className={col.className}>
                          {col.render ? col.render(row) : (row[col.key] ?? "--")}
                        </td>
                      ))}
                      {actions.length > 0 && (
                        <td className="text-end">
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            {actions.map((action, i) => (
                              <button 
                                key={i} 
                                type="button" 
                                onClick={() => action.onClick(row)} 
                                className={`w-32-px h-32-px border-0 rounded d-flex align-items-center justify-content-center transition-all ${action.className}`}
                              >
                                <Icon icon={action.icon} />
                              </button>
                            ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* FOOTER: Updated Pagination */}
        {pagination && !loading && data.length > 0 && (
          <div className="card-footer d-flex flex-wrap align-items-center justify-content-between bg-base border-top">
            <span className="text-secondary-light text-sm">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
            </span>
            <ul className="pagination mb-0 gap-1">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link border-0 bg-neutral-100 radius-8" 
                  onClick={() => page > 1 && onPageChange(page - 1)}
                  disabled={page === 1}
                >
                  <Icon icon="lucide:chevron-left" />
                </button>
              </li>
              
              {getPaginationGroup().map((item, i) => (
                <li key={i} className={`page-item ${page === item ? 'active' : ''} ${item === "..." ? 'disabled' : ''}`}>
                  {item === "..." ? (
                    <span className="page-link border-0 bg-neutral-100 radius-8">...</span>
                  ) : (
                    <button 
                      className={`page-link border-0 radius-8 ${page === item ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-secondary-light'}`} 
                      onClick={() => onPageChange(item)}
                    >
                      {item}
                    </button>
                  )}
                </li>
              ))}

              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link border-0 bg-neutral-100 radius-8" 
                  onClick={() => page < totalPages && onPageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  <Icon icon="lucide:chevron-right" />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default DataTable;